/**
 * Сигналы звонка на Web Audio (изменение v1.14): без аудиофайлов и сетевых запросов.
 *
 * Гудки — по российскому стандарту: 425 Гц, «контроль посылки вызова» 0,8 с / 3,2 с,
 * «занято» 0,35 с / 0,35 с. Входящий — мягкий двухнотный перезвон с вибрацией на телефоне.
 * Браузер разрешает звук только после жеста пользователя: `unlockTones()` вызывается в
 * обработчике «Позвонить» / «Ответить»; если звук всё же заблокирован, остаются вибрация и
 * картинка — обходить автоплей запрещено.
 */

export type ToneKind = 'ringback' | 'ringtone' | 'busy' | 'ended'

interface ToneStep {
  frequencies: number[]
  seconds: number
  gain: number
}

interface TonePattern {
  steps: ToneStep[]
  /** Пауза после шага цикла. */
  pause: number
  /** Сколько раз проиграть; 0 — до остановки. */
  times: number
  vibrate?: number[]
}

const PATTERNS: Record<ToneKind, TonePattern> = {
  ringback: { steps: [{ frequencies: [425], seconds: 0.8, gain: 0.12 }], pause: 3.2, times: 0 },
  busy: { steps: [{ frequencies: [425], seconds: 0.35, gain: 0.12 }], pause: 0.35, times: 6 },
  ended: { steps: [{ frequencies: [425], seconds: 0.2, gain: 0.1 }], pause: 0.2, times: 3 },
  ringtone: {
    steps: [
      { frequencies: [659.25, 1318.5], seconds: 0.18, gain: 0.16 },
      { frequencies: [880, 1760], seconds: 0.18, gain: 0.16 },
      { frequencies: [659.25, 1318.5], seconds: 0.18, gain: 0.16 },
      { frequencies: [880, 1760], seconds: 0.32, gain: 0.16 },
    ],
    pause: 1.6,
    times: 0,
    vibrate: [700, 300, 700],
  },
}

type AudioContextClass = typeof AudioContext

let context: AudioContext | null = null

function audio(): AudioContext | null {
  if (context) return context
  const Ctor =
    (globalThis as { AudioContext?: AudioContextClass }).AudioContext ??
    (globalThis as { webkitAudioContext?: AudioContextClass }).webkitAudioContext
  if (!Ctor) return null
  try {
    context = new Ctor()
  } catch {
    context = null
  }
  return context
}

/** Вызывается в обработчике жеста: после него браузер разрешает звук. */
export function unlockTones(): void {
  const ctx = audio()
  if (ctx && ctx.state === 'suspended') void ctx.resume().catch(() => undefined)
}

function scheduleStep(ctx: AudioContext, step: ToneStep, at: number, nodes: AudioNode[]): void {
  const gain = ctx.createGain()
  gain.gain.setValueAtTime(0, at)
  // Плавные фронты — без щелчков в начале и в конце сигнала.
  gain.gain.linearRampToValueAtTime(step.gain, at + 0.02)
  gain.gain.setValueAtTime(step.gain, at + step.seconds - 0.03)
  gain.gain.linearRampToValueAtTime(0, at + step.seconds)
  gain.connect(ctx.destination)
  nodes.push(gain)
  for (const frequency of step.frequencies) {
    const oscillator = ctx.createOscillator()
    oscillator.type = 'sine'
    oscillator.frequency.setValueAtTime(frequency, at)
    oscillator.connect(gain)
    oscillator.start(at)
    oscillator.stop(at + step.seconds + 0.01)
    nodes.push(oscillator)
  }
}

/** Проигрывает сигнал; возвращает функцию остановки. Повторный вызов остановки безвреден. */
export function playTone(kind: ToneKind): () => void {
  const pattern = PATTERNS[kind]
  const ctx = audio()
  const nodes: AudioNode[] = []
  let timer: ReturnType<typeof setTimeout> | null = null
  let stopped = false
  let played = 0
  const cycle = pattern.steps.reduce((sum, step) => sum + step.seconds, 0) + pattern.pause

  const run = (): void => {
    if (stopped) return
    if (ctx && ctx.state !== 'closed') {
      if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined)
      let at = ctx.currentTime + 0.02
      for (const step of pattern.steps) {
        scheduleStep(ctx, step, at, nodes)
        at += step.seconds
      }
    }
    if (pattern.vibrate) globalThis.navigator?.vibrate?.(pattern.vibrate)
    played += 1
    if (pattern.times === 0 || played < pattern.times) timer = setTimeout(run, cycle * 1000)
  }
  run()

  return () => {
    if (stopped) return
    stopped = true
    if (timer) clearTimeout(timer)
    if (pattern.vibrate) globalThis.navigator?.vibrate?.(0)
    for (const node of nodes) {
      try {
        const source = node as Partial<OscillatorNode>
        if (typeof source.stop === 'function') source.stop()
        node.disconnect()
      } catch {
        /* узел уже остановлен */
      }
    }
  }
}
